import { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { LuxuryWaves, SubtleWaves } from "@/components/ui/waves";
import { useMouseTracking } from "@/hooks/useMouseTracking";
import { useAdvancedScroll } from "@/hooks/useAdvancedScroll";
import { useSiteContent } from "@/hooks/useSiteContent";
import heroBlueLotusBg from "@/assets/hero-blue-lotus-bg.jpg";

export const Hero = () => {
  const { mousePosition, isMouseMoving } = useMouseTracking();
  const { scrollY, scrollProgress, scrollVelocity } = useAdvancedScroll();
  const [logoVisible, setLogoVisible] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [effectsReady, setEffectsReady] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const { content } = useSiteContent('hero');

  // Pre-calculate star positions to avoid Math.random() during render
  const starPositions = useMemo(() => 
    Array.from({ length: 20 }, () => ({
      size: Math.random() * 2 + 1,
      color: Math.floor(Math.random() * 3),
      left: Math.random() * 100,
      top: Math.random() * 100,
    })),
    []
  );

  useEffect(() => {
    const timer = setTimeout(() => setLogoVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);
  // Detect reduced motion preference
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReducedMotion(media.matches);
    onChange();
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    // Cache window width check to avoid forced reflow during render
    const updateIsDesktop = () => {
      setIsDesktop(window.innerWidth > 640);
    };
    
    updateIsDesktop();
    window.addEventListener('resize', updateIsDesktop, { passive: true });
    
    return () => window.removeEventListener('resize', updateIsDesktop);
  }, []);
  
  useEffect(() => {
    // Defer decorative effects until after initial content paint
    const timer = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setEffectsReady(true);
      });
    });
    return () => cancelAnimationFrame(timer);
  }, []);

  // Scroll-local bloom progress: fully blooms within first ~350px
  const heroBloom = Math.max(0, Math.min(1, (scrollY - 50) / 350));

  const scrollToProducts = () => {
    const productsSection = document.getElementById("products");
    productsSection?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToMission = () => {
    const missionSection = document.getElementById("mission");
    missionSection?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden section-light">
      {/* Soft Feminine Background Layers */}
      <div className="absolute inset-0">
        <SubtleWaves className="opacity-50" />
      </div>

      {/* Blue Lotus Background Layer */}
      <div className="absolute inset-0 opacity-15">
        <img
          src={heroBlueLotusBg}
          alt="Blue lotus flowers background"
          className="w-full h-full object-cover"
          loading="eager"
        />
      </div>

      {/* Soft gradient overlay for warmth */}
      <div className="absolute inset-0 bg-gradient-cream opacity-90" />

      {/* LAYER 1: Optimized Starfield - 20 GPU-Accelerated Stars */}
      {effectsReady && !reducedMotion && (
        <div 
          className="absolute inset-0"
          style={{
            transform: `translate3d(${mousePosition.x * 3}px, ${scrollY * 0.1}px, 0)`,
            willChange: 'transform',
          }}
        >
          <div className="absolute inset-0">
            {starPositions.map((star, i) => (
              <div
                key={i}
                className="absolute rounded-full star-optimized"
                style={{
                  width: `${star.size}px`,
                  height: `${star.size}px`,
                  background: star.color === 0 
                    ? 'hsl(var(--gold-glow))' 
                    : star.color === 1 
                      ? 'hsl(var(--lavender))' 
                      : 'hsl(var(--cream))',
                  left: `${star.left}%`,
                  top: `${star.top}%`,
                  opacity: scrollProgress > 0.3 ? 0.8 : 0.4,
                  transform: `translate3d(0, 0, 0) scale(${scrollProgress > 0.5 ? 1.2 : 1})`,
                  willChange: 'transform, opacity',
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* LAYER 2: Simplified Moon System */}
      {effectsReady && !reducedMotion && (
        <div 
          className="absolute inset-0"
          style={{
            transform: `translate3d(0, ${scrollY * 0.2}px, 0) rotate(${scrollProgress > 0.5 ? 30 : 0}deg)`,
            willChange: 'transform',
          }}
        >
          <div 
            className="absolute top-16 right-16 w-32 h-32 rounded-full bg-gradient-gold-glow opacity-20 moon-optimized"
            style={{
              transform: `translate3d(0, 0, 0) scale(${scrollProgress > 0.3 ? 1.1 : 1})`,
              willChange: 'transform',
            }}
          />
        </div>
      )}

      {/* LAYER 3: Atmospheric Waves */}
      {effectsReady && !reducedMotion && (
        <div 
          className="absolute inset-0"
          style={{
            transform: `translateY(${scrollY * 0.4}px)`,
            opacity: 0.6 + mousePosition.y * 0.2,
          }}
        >
          <div 
            className="absolute w-full h-full"
            style={{
              background: `
                radial-gradient(ellipse 1200px 500px at 0% 50%, hsl(var(--lavender) / ${0.3 + scrollProgress * 0.2}) 0%, transparent 70%),
                radial-gradient(ellipse 900px 400px at 100% 80%, hsl(var(--purple-soft) / ${0.2 + scrollProgress * 0.3}) 0%, transparent 70%),
                radial-gradient(ellipse 700px 300px at 50% 20%, hsl(var(--gold) / ${0.15 + scrollProgress * 0.25}) 0%, transparent 70%)
              `,
              animation: 'wave-flow 60s linear infinite',
              filter: `hue-rotate(${scrollProgress * 30}deg)`,
            }}
          />
        </div>
      )}

      {/* LAYER 4: MOBILE OPTIMIZED LOTUS */}
      {effectsReady && !reducedMotion && (
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `translate3d(0, 0, 0) scale(${scrollProgress > 0.5 ? 1.1 : 1})`,
            willChange: 'transform',
          }}
        >
          <div className="relative lotus-container-mobile">
            {/* Single Optimized Glow Layer - Reduced for Brand Clarity */}
            <div className="absolute inset-0 lotus-glow-single">
              <div className="absolute inset-0 rounded-full bg-gradient-gold-glow blur-xl scale-125 opacity-15 lotus-breathing" />
            </div>
            
            {/* MOBILE OPTIMIZED SVG LOTUS - Responsive Sizing */}
            <svg
              className="relative z-10 lotus-svg-mobile w-72 sm:w-96 md:w-[500px] lg:w-[600px] h-72 sm:h-96 md:h-[500px] lg:h-[600px]"
              viewBox="0 0 300 300"
              fill="currentColor"
              style={{
                filter: `drop-shadow(0 0 15px hsl(var(--gold) / 0.4))`,
                willChange: 'transform',
              }}
            >
              {/* 8 Outer Petals - Full Bloom Sequence */}
              {[0, 45, 90, 135, 180, 225, 270, 315].map((rotation, index) => {
                 const petalProgress = Math.max(0, heroBloom - index * 0.08);
                 const finalOpacity = Math.min(1, 0.15 + petalProgress * 2.2);
                 
                 return (
                   <path 
                     key={`outer-${index}`}
                     d="M150 50 Q100 100 150 150 Q200 100 150 50" 
                     className="text-gold lotus-petal-optimized"
                     style={{
                       transformOrigin: '150px 150px',
                       transform: `rotate(${rotation}deg) translate3d(0, 0, 0) scale(${1 + petalProgress * 0.1})`,
                       opacity: finalOpacity,
                       willChange: 'opacity, transform',
                     }}
                   />
                 );
               })}
              
              {/* 8 Inner Petals - Full Bloom Sequence */}
              {[22.5, 67.5, 112.5, 157.5, 202.5, 247.5, 292.5, 337.5].map((rotation, index) => {
                 const petalProgress = Math.max(0, heroBloom - 0.12 - index * 0.08);
                 const finalOpacity = Math.min(1, 0.1 + petalProgress * 2.8);
                 
                 return (
                   <path 
                     key={`inner-${index}`}
                     d="M150 75 Q125 115 150 150 Q175 115 150 75" 
                     className="text-gold-light lotus-petal-inner-optimized"
                     style={{
                       transformOrigin: '150px 150px',
                       transform: `rotate(${rotation}deg) translate3d(0, 0, 0) scale(${1 + petalProgress * 0.12})`,
                       opacity: finalOpacity,
                       willChange: 'opacity, transform',
                     }}
                   />
                 );
               })}
              
              {/* Center - Mouse-Reactive Only */}
              <circle 
                cx="150" 
                cy="150" 
                r="20" 
                className="text-gold-glow lotus-center-optimized" 
                fill="currentColor" 
                style={{ 
                  transform: `translate3d(${mousePosition.x * 2}px, ${mousePosition.y * 1}px, 0)`,
                  willChange: 'transform',
                }} 
              />
            </svg>
          </div>
        </div>
      )}

      {/* Premium Hero Content - Mobile Optimized */}
      <div className="relative z-10 text-center max-w-4xl mx-auto px-4 sm:px-6">
        <h1 className={`font-serif text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gold mb-4 sm:mb-6 text-glow transition-all duration-1000 ${
          logoVisible ? 'opacity-100' : 'opacity-0'
        }`}>
          LUNSSENCE
        </h1>
        
        <p className="text-lg sm:text-xl md:text-2xl text-foreground mb-3 sm:mb-4 animate-fade-in-up [animation-delay:0.2s] font-medium">
          {content?.subtitle || 'Jouw ritueel begint hier'}
        </p>
        
        <div className="w-24 sm:w-32 h-1 bg-gradient-gold-glow mx-auto mb-6 sm:mb-8 animate-fade-in-up [animation-delay:0.4s] rounded-full shadow-gold-glow" />
        
        <p className="text-base sm:text-lg md:text-xl text-foreground/80 max-w-2xl mx-auto mb-8 sm:mb-12 leading-relaxed animate-fade-in-up [animation-delay:0.6s] font-light px-2">
          {content?.description || 'Ervaar de betovering van onze zorgvuldig geselecteerde theemelanges'}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-stretch sm:items-center animate-fade-in-up [animation-delay:0.8s] px-2">
          <Button 
            onClick={scrollToProducts}
            className="btn-hero text-base sm:text-lg px-8 sm:px-12 py-4 sm:py-6 magnetic-button group min-h-[44px] touch-manipulation w-full sm:w-auto"
            style={{
              transform: isMouseMoving && isDesktop ? `translate(${mousePosition.x * 2}px, ${mousePosition.y * 1}px)` : 'translate(0, 0)',
            }}
          >
            <span className="relative z-10">{content?.metadata?.cta_text || 'Begin jouw ritueel'}</span>
            <div className="absolute inset-0 bg-gradient-gold-glow opacity-0 @media(hover:hover):group-hover:opacity-30 transition-opacity duration-300 rounded-full blur-sm" />
          </Button>
          
          <Button 
            variant="outline" 
            onClick={scrollToMission}
            className="btn-secondary text-base sm:text-lg px-8 sm:px-12 py-4 sm:py-6 magnetic-button group min-h-[44px] touch-manipulation w-full sm:w-auto"
            style={{
              transform: isMouseMoving && isDesktop ? `translate(${mousePosition.x * -1}px, ${mousePosition.y * 0.5}px)` : 'translate(0, 0)',
            }}
          >
            <span className="relative z-10">{content?.metadata?.secondary_cta_text || 'Ontdek Meer'}</span>
            <div className="absolute inset-0 border border-gold/50 opacity-0 @media(hover:hover):group-hover:opacity-60 transition-opacity duration-300 rounded-full" />
          </Button>
        </div>
      </div>

      {/* Premium Double Chevron Scroll Indicator - Centered with Golden Pulse */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 interactive-compass group cursor-pointer" onClick={scrollToProducts}>
        <div className="relative w-16 h-16 flex items-center justify-center">
          <div className="absolute inset-0 w-16 h-16 rounded-full border border-gold/30" style={{ animation: 'golden-pulse 2s ease-in-out infinite' }} />
          <div className="absolute inset-2 w-12 h-12 rounded-full bg-gradient-gold-glow opacity-20" style={{ animation: 'golden-pulse 2s ease-in-out infinite 0.5s' }} />
          {/* Double Chevron (↓) */}
          <div className="relative z-10 flex flex-col items-center justify-center -space-y-2 group-hover:scale-110 transition-transform duration-300" style={{ filter: 'drop-shadow(0 0 8px hsl(var(--gold)))', animation: 'compass-bounce 2s ease-in-out infinite' }}>
            <ChevronDown className="w-6 h-6 text-gold" style={{ animation: 'golden-pulse 2s ease-in-out infinite' }} />
            <ChevronDown className="w-6 h-6 text-gold" style={{ animation: 'golden-pulse 2s ease-in-out infinite 0.3s' }} />
          </div>
        </div>
      </div>

      {/* LAYER 5: Optimized Floating Petals */}
      {effectsReady && !reducedMotion && (
        <div className="floating-petals-optimized">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="petal-floating rounded-full"
              style={{
                left: `${10 + i * 15}%`,
                opacity: scrollProgress > 0.3 ? 0.6 : 0.3,
                willChange: 'transform, opacity',
              }}
            />
          ))}
        </div>
      )}

    </section>
  );
};